import { FloatingRailSlot } from "@/components/molecules/FloatingRailSlot/FloatingRailSlot";
import BottomSheet from "@/design-system/BottomSheet/BottomSheet";
import { Button } from "@/design-system/Button/Button";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import ComparePage from "@/pages/Compare/ComparePage";
import { AddPlayerButton } from "@/pages/Compare/mobile/CompareMobile";
import {
  globalStateSelector,
  setCompareSheetOpen,
} from "@/redux/globalState/globalStateSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import {
  clearComparePlayers,
  comparePlayersSelector,
} from "@repo/shared/redux";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

/**
 * Floating "compare mode" indicator, mounted globally in RootLayout. Visible on
 * every page (except /compare itself) while players are staged for comparison.
 * Clicking the button opens the comparison in a bottom sheet; the sheet's
 * expand button grows it to full screen in place (no navigation, so nothing
 * remounts). Clicking the widget's X exits compare mode and clears the staged
 * players.
 */
export const CompareWidget = () => {
  const players = useSelector(comparePlayersSelector);
  const dispatch = useDispatch();
  const { t } = useTranslation("compare");
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [searching, setSearching] = useState(false);
  // In redux so a page can stage players and open the sheet in one go.
  const { isCompareSheetOpen: sheetOpen, compareExitOnClose } =
    useSelector(globalStateSelector);
  const setSheetOpen = (open: boolean) => dispatch(setCompareSheetOpen(open));
  const isInSquadBuilder = useLocation().pathname.startsWith("/squad-builder");
  // Touch devices skip the 90dvh stage entirely — the sheet opens full
  // screen so the comparison feels like its own screen.
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  // The page under the sheet must not keep scrolling while it's open.
  useScrollLock(sheetOpen);

  // "Added to cart" toast: when the staged set grows, name the newest player
  // above the pill for 2 seconds, fading in and out. (Adds are pushes, so
  // newest = last.) The id makes re-adding the same player re-trigger the
  // timers even though the message text is identical.
  const [toast, setToast] = useState<{ id: number; message: string } | null>(
    null,
  );
  const [toastVisible, setToastVisible] = useState(false);
  const toastId = useRef(0);
  const prevCount = useRef(players.length);
  useEffect(() => {
    if (players.length > prevCount.current) {
      const added = players[players.length - 1];
      setToast({
        id: ++toastId.current,
        message: t("widget.addedToast", { name: added.cardName }),
      });
    }
    prevCount.current = players.length;
  }, [players]);
  useEffect(() => {
    if (!toast) return;
    // Double rAF: the opacity-0 mount must be committed before flipping to
    // opacity-100, or there's no fade-in transition.
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setToastVisible(true)),
    );
    const hide = setTimeout(() => setToastVisible(false), 2000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(hide);
    };
  }, [toast]);

  // Keep the sheet mounted while open even if the last player is removed, so
  // it doesn't vanish mid-interaction (the empty add-slot still renders).
  if (
    (players.length === 0 && !sheetOpen) ||
    location.pathname.startsWith("/compare")
  ) {
    return null;
  }

  const closeSheet = () => {
    setSheetOpen(false);
    setExpanded(false);
    setSearching(false);
    // A one-off comparison (e.g. from My Club) leaves compare mode on close.
    if (compareExitOnClose) dispatch(clearComparePlayers());
  };

  // Backdrop click / Escape dismiss one layer at a time: search phase first,
  // then the sheet itself.
  const dismissSheet = () => {
    if (searching) {
      setSearching(false);
      return;
    }
    closeSheet();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeSheet();
    dispatch(clearComparePlayers());
  };

  return (
    <>
      {players.length > 0 && (
        <FloatingRailSlot
          className={cn(
            "bottom-[var(--ad-gutter)]",
            isInSquadBuilder && isTouchDevice && "pb-20",
          )}
        >
          {toast && (
            <div
              className={cn(
                "absolute bottom-full left-0 mb-1 whitespace-nowrap rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-xs font-medium text-neutral-200 shadow-lg transition-opacity duration-500",
                toastVisible ? "opacity-100" : "opacity-0",
              )}
              onTransitionEnd={() => {
                // Unmount once the fade-out finishes.
                if (!toastVisible) setToast(null);
              }}
            >
              {toast.message}
            </div>
          )}
          <Button
            variant="primary"
            size="medium"
            icon={<X onClick={handleClear} className="cursor-pointer" />}
            iconPosition="right"
            onClick={(e) => {
              setSheetOpen(true);
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {t("widget.compareCount", { amount: players.length })}
          </Button>
        </FloatingRailSlot>
      )}
      {/* Touch: the sheet's own header, minimizing back down to the page
          (players stay staged on the widget). It goes while the search phase
          is up — that view's back button and input are the whole header. */}
      <BottomSheet
        isOpen={sheetOpen}
        onClose={dismissSheet}
        className={cn(
          "bg-neutral-900",
          expanded || isTouchDevice
            ? "h-dvh max-w-full rounded-none"
            : "h-[90dvh] max-w-content",
        )}
        header={
          isTouchDevice && !searching
            ? {
                title: t("title"),
                closeLabel: t("header.close"),
                action: <AddPlayerButton onClick={() => setSearching(true)} />,
                onClose: closeSheet,
              }
            : undefined
        }
      >
        <ComparePage
          embedded
          expanded={expanded}
          onToggleExpand={() => setExpanded((prev) => !prev)}
          onClose={closeSheet}
          searching={searching}
          onSearchingChange={setSearching}
        />
      </BottomSheet>
    </>
  );
};
